import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type IAuthenticationCallback,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

import type {
  AssistantResponse,
  ChargeSort,
  CostDimension,
  CostFilters,
  DashboardResponse,
} from "../shared/contracts";

export interface RuntimeConfig {
  apiBasePath: string;
  userPoolId?: string;
  userPoolClientId?: string;
}

export interface SavedView {
  id: string;
  name: string;
  filters: CostFilters;
  groupBy: CostDimension;
  createdAt: string;
}

export type AuthenticationStep =
  "authenticated" | "newPasswordRequired" | "mfaRequired";

export interface DashboardApi {
  prepare(): Promise<boolean>;
  login(email: string, password: string): Promise<AuthenticationStep>;
  completeNewPassword(password: string): Promise<AuthenticationStep>;
  completeMfa(code: string): Promise<AuthenticationStep>;
  signOut(): void;
  dashboard(
    filters: CostFilters,
    groupBy: CostDimension,
    sort: ChargeSort,
  ): Promise<DashboardResponse>;
  assistant(
    message: string,
    filters: CostFilters,
    conversationId: string,
  ): Promise<AssistantResponse>;
  listViews(): Promise<SavedView[]>;
  saveView(
    name: string,
    filters: CostFilters,
    groupBy: CostDimension,
  ): Promise<SavedView>;
}

function session(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.getSession((error: Error | null, value: CognitoUserSession | null) => {
      if (error || !value)
        reject(error ?? new Error("Cognito session is unavailable"));
      else resolve(value);
    });
  });
}

export class WatchpointApi implements DashboardApi {
  private readonly pool?: CognitoUserPool;
  private token?: string;
  private pendingUser?: CognitoUser;
  private pendingMfaType?: string;

  constructor(private readonly config: RuntimeConfig) {
    if (config.userPoolId && config.userPoolClientId) {
      this.pool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.userPoolClientId,
      });
    }
  }

  async prepare(): Promise<boolean> {
    const user = this.pool?.getCurrentUser();
    if (!user) return false;
    try {
      this.token = (await session(user)).getIdToken().getJwtToken();
      return true;
    } catch {
      return false;
    }
  }

  private authenticationCallbacks(
    resolve: (step: AuthenticationStep) => void,
    reject: (reason?: unknown) => void,
    clearChallengeOnFailure = true,
  ): IAuthenticationCallback {
    return {
      onSuccess: (authenticated) => {
        this.token = authenticated.getIdToken().getJwtToken();
        this.pendingUser = undefined;
        this.pendingMfaType = undefined;
        resolve("authenticated");
      },
      onFailure: (error: unknown) => {
        if (clearChallengeOnFailure) {
          this.pendingUser = undefined;
          this.pendingMfaType = undefined;
        }
        reject(error);
      },
      newPasswordRequired: () => resolve("newPasswordRequired"),
      mfaRequired: (challengeName) => {
        this.pendingMfaType = challengeName;
        resolve("mfaRequired");
      },
      totpRequired: (challengeName) => {
        this.pendingMfaType = challengeName;
        resolve("mfaRequired");
      },
    };
  }

  async login(email: string, password: string): Promise<AuthenticationStep> {
    if (!this.pool) throw new Error("Cognito runtime configuration is missing");
    const user = new CognitoUser({ Username: email, Pool: this.pool });
    this.pendingUser = user;
    const details = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    return new Promise<AuthenticationStep>((resolve, reject) => {
      user.authenticateUser(
        details,
        this.authenticationCallbacks(resolve, reject),
      );
    });
  }

  async completeNewPassword(password: string): Promise<AuthenticationStep> {
    const user = this.pendingUser;
    if (!user) throw new Error("No new-password challenge is active");
    return new Promise<AuthenticationStep>((resolve, reject) => {
      user.completeNewPasswordChallenge(
        password,
        {},
        this.authenticationCallbacks(resolve, reject, false),
      );
    });
  }

  async completeMfa(code: string): Promise<AuthenticationStep> {
    const user = this.pendingUser;
    if (!user) throw new Error("No MFA challenge is active");
    return new Promise<AuthenticationStep>((resolve, reject) => {
      user.sendMFACode(
        code,
        {
          onSuccess: (authenticated) => {
            this.token = authenticated.getIdToken().getJwtToken();
            this.pendingUser = undefined;
            this.pendingMfaType = undefined;
            resolve("authenticated");
          },
          onFailure: reject,
        },
        this.pendingMfaType,
      );
    });
  }

  signOut(): void {
    this.pool?.getCurrentUser()?.signOut();
    this.token = undefined;
    this.pendingUser = undefined;
    this.pendingMfaType = undefined;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.config.apiBasePath}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
      };
      throw new Error(
        body.message ?? `Request failed with status ${response.status}`,
      );
    }
    return (await response.json()) as T;
  }

  async dashboard(
    filters: CostFilters,
    groupBy: CostDimension,
    sort: ChargeSort,
  ): Promise<DashboardResponse> {
    return this.request<DashboardResponse>("/dashboard", {
      method: "POST",
      body: JSON.stringify({ filters, groupBy, sort }),
    });
  }

  async assistant(
    message: string,
    filters: CostFilters,
    conversationId: string,
  ): Promise<AssistantResponse> {
    return this.request<AssistantResponse>("/assistant", {
      method: "POST",
      body: JSON.stringify({ message, filters, conversationId }),
    });
  }

  async listViews(): Promise<SavedView[]> {
    return this.request<SavedView[]>("/views");
  }

  async saveView(
    name: string,
    filters: CostFilters,
    groupBy: CostDimension,
  ): Promise<SavedView> {
    return this.request<SavedView>("/views", {
      method: "POST",
      body: JSON.stringify({ name, filters, groupBy }),
    });
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch("/runtime-config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      "runtime-config.json is unavailable. Locally, copy config/runtime-config.example.json to config/runtime-config.json and fill in the deployed values.",
    );
  }
  return (await response.json()) as RuntimeConfig;
}
